import { token } from '@atlaskit/tokens';
import { FieldNameInlineContainer } from '@databases/field-name/FieldNameWithActions';
import { InlineEditWrapperContainer } from '@databases/inline-edit/InlineEditWrapper';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { TABLE_BORDER_RADIUS } from '@databases/styles';
import React, { type FunctionComponent, useRef } from 'react';
import styled from 'styled-components';

import { gridTable, newTableBorderWidth, subtleBorder, SubtleTable } from '../styled';

import DatabaseEntryRow from './DatabaseEntryRow';

export const Container = styled.div`
  min-height: 0;
  overflow: auto;
  box-sizing: border-box;
  flex-grow: 1;
  margin-bottom: ${token('space.100', '8px')};
`;

export const Table = styled(SubtleTable)<{ fieldNameWidth?: number }>`
  ${gridTable}
  grid-template-columns: ${(props) => `${props.fieldNameWidth ?? 260}px`} 1fr;
  overflow-wrap: anywhere;

  > * > tr > * {
    display: flex;
    flex-direction: column;
    min-height: 42px;
    min-width: 0;
    padding: 0;
    box-sizing: border-box;

    &:last-child {
      border-right: ${subtleBorder};
      border-width: ${newTableBorderWidth}px;
    }

    > ${InlineEditWrapperContainer} {
      flex-grow: 1;

      ${FieldNameInlineContainer} {
        box-sizing: border-box;
        padding: ${token('space.100', '8px')};
        font: ${token('font.heading.xxsmall')};
      }
    }
  }

  > * > tr {
    &:first-child {
      th {
        border-start-start-radius: ${TABLE_BORDER_RADIUS};
      }
      td {
        border-start-end-radius: ${TABLE_BORDER_RADIUS};
      }
    }
    &:last-child {
      th {
        border-end-start-radius: ${TABLE_BORDER_RADIUS};
      }
      td {
        border-end-end-radius: ${TABLE_BORDER_RADIUS};
      }
    }
  }
`;

export interface DatabaseEntryProps {
  contentId: string;
  entryId: string;
  isContentEditable?: boolean;
  fieldNameWidth?: number;
  /** If true, focus the first value. */
  autoFocus?: boolean;
  isEmbedEntryView?: boolean;
  /** If true, displays greyed out / color.disabled for database values */
  showDisabledValue?: boolean;
}

/**
 * Renders a table that shows all the field names and values of a database entry. The field names and values can be edited
 * using inline edit fields, and new fields can be added. Any edits will cause a database update to be drafted automatically.
 */
const DatabaseEntry: FunctionComponent<DatabaseEntryProps> = ({
  contentId,
  entryId,
  isContentEditable = false,
  fieldNameWidth,
  autoFocus,
  isEmbedEntryView,
  showDisabledValue = false,
}) => {
  const [fieldIds, operations, isLocked] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      includeDeleted: true,
    });
    return [
      databaseDraft.fields,
      databaseDraft.operations,
      databaseDraft.meta.isLocked,
    ] as const;
  });

  const canEditContent = isContentEditable && operations.updateEntries;
  const canEditFields =
    isContentEditable && operations.updateMetadata && !isLocked && !isEmbedEntryView;

  const editingIds = useRef(new Set<string>());

  return (
    <Container>
      <Table data-testid={'database-entry-table'} fieldNameWidth={fieldNameWidth}>
        <tbody>
          {fieldIds.map((fieldId, i) => (
            <DatabaseEntryRow
              key={fieldId}
              contentId={contentId}
              entryId={entryId}
              fieldId={fieldId}
              isContentEditable={isContentEditable}
              canEditContent={canEditContent}
              canEditFields={canEditFields}
              editingIds={editingIds}
              autoFocus={autoFocus && i === 0}
              showDisabledValue={showDisabledValue}
            />
          ))}
        </tbody>
      </Table>
    </Container>
  );
};

export default DatabaseEntry;
